import { Button, Field, Form } from '@cluey/cluey-components';
import { useMutation } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import isError from 'lodash/isError';
import { useState } from 'react';
import { Redirect, useLocation } from 'react-router';
import { z } from 'zod';

import { PATHS, useSignupStore } from '.';
import { api } from '../../../../api';
import { ddLogger, ddLoggerLevel } from '../../../../logging';
import { PATH_HUB_RESET_PASSWORD_REQUEST } from '../../../../util/pagePath';
import RightColumn from '../RightColumn';
import TwoColumnLayout from '../TwoColumnLayout';
import { BackButton, InlineLoader } from '../common';
import useNavigateWithSearch from '../useNavigateWithSearch';
import ResendCodeButton from './ResendCodeButton';
import Stepper from './Stepper';

export const SignUpVerifyPage = () => {
  const { email, username, token } = useSignupStore(({ email, username, token }) => ({ email, username, token }));
  const [tempPassword, setTempPassword] = useState('');
  const navigateWithSearch = useNavigateWithSearch();
  const location = useLocation();
  const [error, setError] = useState('');

  const { mutate, isLoading } = useMutation({
    mutationFn: async (tempPassword: string) => {
      // confirm signup with AWS, this verifies the account and creates a lead in sf
      await Auth.confirmSignUp(username, tempPassword);
      // decode the resulting token to grab the temp password
      try {
        const decoded = JSON.parse(Buffer.from(token, 'base64').toString());
        // sign in with the temp password, ready to change it
        return Auth.signIn(username, decoded.tmpPassword);
      } catch (error) {
        // handles an edge case where user signs up, but returns to the login page somehow and tries to login
        if (error instanceof Error && error.message.includes('Unexpected end of JSON input')) {
          navigateWithSearch(PATHS.ROOT_PATH);
        } else {
          throw error;
        }
      }
    },
    onSuccess: () => {
      api.account.details.resetQueries();
      navigateWithSearch(PATHS.PASSWORD_PATH);
    },
    onError: (error) => {
      // handles an edge case
      if (isError(error) && error.message.includes('User cannot be confirmed. Current status is CONFIRMED')) {
        navigateWithSearch(PATH_HUB_RESET_PASSWORD_REQUEST, { emailParam: email });
      }
      console.error('SignUpVerifyPage error - ', { error });
      // @ts-ignore
      if (error.code && error.code === 'CodeMismatchException') {
        setError('Invalid verification code provided, please try again.');
      } else {
        ddLogger({
          level: ddLoggerLevel.ERROR,
          label: 'Confirm signup failed',
          data: { email, username, token },
          error,
        });
        setError('Something went wrong. Please try again.');
      }
    },
  });

  const handleSubmit = () => {
    const tempPasswordResult = z.string().min(1, 'Please enter your temporary password.').safeParse(tempPassword);
    if (tempPasswordResult.success === true) {
      mutate(tempPasswordResult.data.trim());
    } else {
      setError(tempPasswordResult.error.errors[0].message);
    }
  };

  const resendCode = () => {
    Auth.resendSignUp(username);
  };

  if (!username) return <Redirect to={`${PATHS.ROOT_PATH}${location.search}`} />;

  const renderLeftColumn = () => (
    <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col overflow-y-auto px-5">
      <div className="mt-10">
        <BackButton />
      </div>
      <div className="mb-6 mt-6">
        <Stepper steps={5} activeStep={2} />
      </div>
      <h2 className="mb-4 font-display text-4xl font-bold">Validate your account</h2>
      <p className="pb-8">Enter the 6-digit code we sent to your email.</p>
      <Form onSubmit={handleSubmit} className="w-full pb-8">
        <div className="w-full pb-8">
          <Field
            label="Verification code"
            type="text"
            value={tempPassword}
            onChange={(value) => {
              setTempPassword(value);
              setError('');
            }}
            disabled={isLoading}
            className=""
            error={error}
          />
          <div className="mt-1 text-sm text-grey-5">Expires in 24 hours</div>
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isLoading}
          className="relative flex items-center justify-center max-sm:w-full"
        >
          <span className={isLoading ? 'opacity-0' : ''}>Continue</span>
          {isLoading && <InlineLoader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />}
        </Button>
      </Form>
      <ResendCodeButton onClick={resendCode} />
    </div>
  );

  const renderRightColumn = () => <RightColumn step={2} />;

  return <TwoColumnLayout renderLeftColumn={renderLeftColumn} renderRightColumn={renderRightColumn} />;
};
